import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { loadGoogleMapsLibrary } from "../../services/googleMapsApi";

const AutoComplete = ({ locationPicked, currentLocation, closeLocation }) => {
    const inputRef = useRef();
    const locationPickedRef = useRef(locationPicked);
    const autocompleteSuggestionRef = useRef(null);
    const sessionTokenConstructorRef = useRef(null);
    const sessionTokenRef = useRef(null);
    const debounceRef = useRef(null);
    const requestIdRef = useRef(0);
    const isMountedRef = useRef(false);
    const [suggestions, setSuggestions] = useState([]);

    useEffect(() => {
        locationPickedRef.current = locationPicked;
    }, [locationPicked]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.value = currentLocation ?? '';
        }
    }, [currentLocation]);

    const resetSearch = () => {
        if (closeLocation) {
            closeLocation();
            return;
        }

        if (inputRef.current) {
            inputRef.current.value = '';
        }

        setSuggestions([]);
    };

    const switchIcon = (switchToDefault) => {
        document.getElementById('location-search-default').hidden = !switchToDefault;
        document.getElementById('location-search-close').hidden = switchToDefault;
        document.getElementById('location-search-reset').hidden = switchToDefault;
    };

    function fetchSuggestions(input) {
        window.clearTimeout(debounceRef.current);

        const trimmedInput = input.trim();
        if (!trimmedInput || !autocompleteSuggestionRef.current) {
            setSuggestions([]);
            return;
        }

        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;

        debounceRef.current = window.setTimeout(async () => {
            try {
                const { suggestions: nextSuggestions } = await autocompleteSuggestionRef.current.fetchAutocompleteSuggestions({
                    input: trimmedInput,
                    sessionToken: sessionTokenRef.current,
                });

                if (!isMountedRef.current || requestIdRef.current !== requestId) return;

                setSuggestions((nextSuggestions ?? []).filter((suggestion) => suggestion.placePrediction));
            } catch (error) {
                if (!isMountedRef.current || requestIdRef.current !== requestId) return;

                console.error("Failed to fetch Google Maps autocomplete suggestions.", error);
                setSuggestions([]);
            }
        }, 250);
    }

    useEffect(() => {
        isMountedRef.current = true;

        const initializeAutocomplete = async () => {
            try {
                const { AutocompleteSessionToken, AutocompleteSuggestion } = await loadGoogleMapsLibrary('places');

                if (!isMountedRef.current) return;

                autocompleteSuggestionRef.current = AutocompleteSuggestion;
                sessionTokenConstructorRef.current = AutocompleteSessionToken;
                sessionTokenRef.current = new AutocompleteSessionToken();

                if (inputRef.current?.value) {
                    fetchSuggestions(inputRef.current.value);
                }
            } catch (error) {
                console.error("Failed to initialize Google Maps autocomplete.", error);
            }
        };

        initializeAutocomplete();

        return () => {
            isMountedRef.current = false;
            requestIdRef.current += 1;
            window.clearTimeout(debounceRef.current);
        };
    }, []);

    const selectSuggestion = async (suggestion) => {
        const prediction = suggestion.placePrediction;
        if (!prediction) return;

        try {
            const place = prediction.toPlace();
            await place.fetchFields({
                fields: ['addressComponents', 'formattedAddress', 'location'],
            });

            if (!isMountedRef.current) return;

            const formattedAddress = place.formattedAddress ?? getSuggestionText(prediction);
            if (inputRef.current) {
                inputRef.current.value = formattedAddress;
            }

            setSuggestions([]);
            if (sessionTokenConstructorRef.current) {
                sessionTokenRef.current = new sessionTokenConstructorRef.current();
            }

            locationPickedRef.current?.(normalizePlace(place, formattedAddress));
        } catch (error) {
            console.error("Failed to resolve Google Maps autocomplete selection.", error);
        }
    };

    const handleBlur = () => {
        switchIcon(true);
        window.setTimeout(() => {
            if (isMountedRef.current) {
                setSuggestions([]);
            }
        }, 150);
    };

    return (
        <div className='location-container'>
            <div id='location-search-default' className='location-search-close'><i className="fas fa-search location-search-icon"></i></div>
            <div id='location-search-close' className='location-search-close' hidden={true}><i className="fas fa-arrow-left location-search-icon"></i></div>
            <input
                id='location-search'
                className='location-search-input'
                ref={inputRef}
                defaultValue={currentLocation}
                placeholder='Search location'
                autoComplete='off'
                onBlur={handleBlur}
                onChange={(event) => fetchSuggestions(event.target.value)}
                onFocus={() => { switchIcon(false) }}
            />
            <div id='location-search-reset' className='location-search-reset' onClick={resetSearch} onMouseDown={(event) => event.preventDefault()} hidden={true}><i className="fas fa-times location-search-icon"></i></div>
            {suggestions.length > 0 && (
                <div className='location-search-suggestions' role='listbox'>
                    {suggestions.map((suggestion) => {
                        const prediction = suggestion.placePrediction;
                        const suggestionText = getSuggestionText(prediction);

                        return (
                            <button
                                type='button'
                                className='pac-item'
                                key={prediction.placeId ?? suggestionText}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => selectSuggestion(suggestion)}
                                role='option'
                            >
                                <span className='pac-item-query'>{suggestionText}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const getSuggestionText = (prediction) => {
    const text = prediction?.text;
    if (typeof text === 'string') return text;

    return text?.text
        ?? text?.toString?.()
        ?? [prediction?.mainText?.text, prediction?.secondaryText?.text].filter(Boolean).join(', ');
};

const normalizePlace = (place, formattedAddress) => {
    const location = place.location;
    const lat = typeof location?.lat === 'function' ? location.lat() : location?.lat;
    const lng = typeof location?.lng === 'function' ? location.lng() : location?.lng;

    return {
        address_components: (place.addressComponents ?? []).map((component) => ({
            long_name: component.longText,
            short_name: component.shortText,
            types: component.types,
        })),
        formatted_address: formattedAddress,
        geometry: {
            location: {
                lat: () => lat,
                lng: () => lng,
            },
        },
    };
};

AutoComplete.propTypes = {
    locationPicked: PropTypes.func.isRequired,
    currentLocation: PropTypes.string,
    closeLocation: PropTypes.func,
};

export default AutoComplete;
